export interface SetScore {
  p1: number;
  p2: number;
}

export interface MatchPlayer {
  memberId: number;
  name: string;
}

export interface MatchRules {
  bestOf: number;
  pointsPerSet: number;
  withAdvantage: boolean;
  betterPoint: number | null;
  setsPlayed?: number | null;
}

export interface SetEntryOptions {
  maxScore: number;
  winnerByLoser: Record<string, number>;
}

export interface SideRating {
  rating: number;
  deltaIfWin: number;
  deltaIfLose: number;
}

export interface MatchRating {
  side1: SideRating;
  side2: SideRating;
  appliedDelta: { side1: number; side2: number } | null;
}

export interface MatchItem {
  id: number;
  status: string;
  groupName: string | null;
  // Numer rundy kolejki rotacyjnej; null/brak w zwykłej kolejce.
  runNumber?: number | null;
  rules: MatchRules;
  setEntry: SetEntryOptions;
  player1Name: string;
  player2Name: string;
  side1: MatchPlayer[];
  side2: MatchPlayer[];
  sets: SetScore[];
  winnerSide: 'SIDE1' | 'SIDE2' | null;
  rating?: MatchRating | null;
}

export interface StandingRow {
  rank: number;
  leaguePlayerId: number;
  name: string;
  played: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  points: number;
  smallPoints: number;
  smallPointsLost: number;
  placementPoints: number;
  rating: number;
  ratingDelta: number;
}

export interface ScoringSystem {
  perSet: boolean;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  walkoverPoints: number;
  dominantMargin: number | null;
  dominantWinPoints: number | null;
  dominantLossPoints: number | null;
}

export interface ScoringGroup {
  id: number | null;
  name: string | null;
  rules: MatchRules;
  scoring?: ScoringSystem;
  matchCount: number;
  playedCount: number;
  standings: StandingRow[];
}

export interface RoundInfo {
  id: number;
  name: string;
  number: number;
  startDate: string;
  shareToken?: string;
  league: { id: number; name: string; discipline: string };
  season: { id: number; number: number; name: string };
}

// Kolejka rotacyjna (americano / mexicano / king of the court) gra rundami
// dokładanymi w trakcie — tego nie ma zwykła kolejka rozpisana z góry.
export interface RotationState {
  format: string;
  formatTitle: string;
  nextRunNumber: number;
  dealtRuns: number;
  missingResults: number;
  canDealNextRun: boolean;
  blockedReason: string | null;
}

export interface ScoringResponse {
  status: string;
  canSubmit: boolean;
  rotation?: RotationState | null;
  round: RoundInfo;
  groups?: ScoringGroup[];
  matches: MatchItem[];
  // Ile z delty ELO niesie mecz tej kolejki (americano 0.5). Liczy API — front tylko pokazuje.
  ratingWeight?: number;
}

export function ratingWeightLabel(ratingWeight: number | undefined): string | null {
  if (ratingWeight == null || ratingWeight === 1) {
    return null;
  }

  return `×${ratingWeight.toString().replace('.', ',')}`;
}

function setCountLabel(rules: MatchRules): string {
  const setsPlayed = rules.setsPlayed ?? null;

  if (setsPlayed === null) {
    return `BO${rules.bestOf}`;
  }

  if (setsPlayed === 1) {
    return '1 set';
  }

  return setsPlayed < 5 ? `${setsPlayed} sety` : `${setsPlayed} setów`;
}

export function rulesLabel(rules: MatchRules): string {
  const ending = rules.withAdvantage
    ? `przewaga 2 pkt${rules.betterPoint === null ? '' : `, cap ${rules.betterPoint}`}`
    : 'bez przewagi';

  return `do ${rules.pointsPerSet} · ${setCountLabel(rules)} · ${ending}`;
}

export function groupFormatLabel(group: ScoringGroup): string {
  const scoring = rulesLabel(group.rules);

  if (!usesPointSystem(group)) {
    return scoring;
  }

  return `${scoring} · better point`;
}

export function smallPointsBalance(row: StandingRow): number {
  return row.smallPoints - row.smallPointsLost;
}

export function usesPointSystem(group: ScoringGroup): boolean {
  return group.scoring?.perSet === true;
}

export function groupKey(group: ScoringGroup): string {
  return group.name ?? '__all__';
}

export function matchesOf(matches: MatchItem[], group: ScoringGroup): MatchItem[] {
  return matches.filter((match) => match.groupName === group.name);
}

export function formatDelta(value: number): string {
  if (value > 0) {
    return `+${value}`;
  }

  return String(value).replace('-', '−');
}

export function deltaTone(value: number): string {
  if (value > 0) {
    return 'scoring-delta scoring-delta--up';
  }

  if (value < 0) {
    return 'scoring-delta scoring-delta--down';
  }

  return 'scoring-delta';
}

export function resolveGroups(response: ScoringResponse): ScoringGroup[] {
  if (response.groups && response.groups.length > 0) {
    return response.groups;
  }

  return response.matches.reduce<ScoringGroup[]>((groups, match) => {
    const name = match.groupName ?? null;
    const existing = groups.find((group) => group.name === name);
    const played = match.winnerSide !== null ? 1 : 0;

    if (existing) {
      existing.matchCount += 1;
      existing.playedCount += played;

      return groups;
    }

    return [
      ...groups,
      { id: null, name, rules: match.rules, matchCount: 1, playedCount: played, standings: [] },
    ];
  }, []);
}
